/* ============================================================
   Component · CTA cursor parallax

   The closing block's cursor badges lean towards the pointer. It is a
   COLLABORATIVE-CANVAS joke made literal: the badges are other people's cursors,
   and other people's cursors move when you do.

   THE POINTER IS TRACKED ACROSS THE WHOLE PAGE, not over the section. Hover
   parallax scoped to its own block is a trick you find by accident and only while
   you are already looking at it; the badges are meant to have been drifting the
   whole way down the page, so that arriving at the block you find a room that was
   already moving. There is nothing to discover and nothing to hover.

   WHAT THIS WRITES: two pixel offsets, `--cta-mx` / `--cta-my`, onto the section.
   Everything else is CSS — which plane travels how far, how it eases, and whether
   it moves at all (see components/_cta.scss). That split matters: the drift
   animation and the parallax are two different motions on one object, and the
   stylesheet is where it is decided how they compose.

   IT USES `translate`, NOT `transform`, AND THAT IS THE WHOLE REASON THIS WORKS.
   The badges already run a keyframe animation on `transform` (the idle drift). A
   second transform would have to be multiplied into the first, which cannot be
   done from a custom property. `translate` is an independent transform property:
   the browser composes it with `transform` for free, so the two motions stack
   without either knowing about the other.

   CHEAP BY CONSTRUCTION. One passive listener; the work is coalesced into a rAF
   and written only while the section is actually on screen. Off screen the
   listener still fires and does nothing — an IntersectionObserver is what decides,
   rather than a per-move bounding-box read.
   ============================================================ */

/* How far the pointer moves the layer, in px, at the very edge of the viewport.
   The two planes are set from this in the stylesheet; this is the budget, and it
   is deliberately modest — the badges are a picture of a room, and a room does not
   swing. */
const RANGE = 34;

function init() {
    const stages = Array.from(document.querySelectorAll('[data-cta-parallax]'));
    if (!stages.length) return;

    /* Motion that follows the pointer is exactly what this request is about, and
       unlike the idle drift there is nothing left to read once it is gone. */
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    /* A coarse pointer has no hover and no continuous position — on a touch screen
       the only "pointer moves" are taps, and following those makes the badges jump
       whenever the page is scrolled with a finger. */
    if (!window.matchMedia('(pointer: fine)').matches) return;

    const visible = new Set();

    const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) visible.add(entry.target);
            else visible.delete(entry.target);
        });
    }, { rootMargin: '10%' });

    stages.forEach((el) => io.observe(el));

    let queued = false;
    let x = 0;
    let y = 0;

    function apply() {
        queued = false;
        visible.forEach((el) => {
            el.style.setProperty('--cta-mx', `${x}px`);
            el.style.setProperty('--cta-my', `${y}px`);
        });
    }

    window.addEventListener('pointermove', (e) => {
        // Normalised to the VIEWPORT, not to the section: the pointer is being read
        // as "where in the room are you standing", and that question has the same
        // answer whether the block is on screen or four screens down.
        const nx = (e.clientX / window.innerWidth) * 2 - 1;
        const ny = (e.clientY / window.innerHeight) * 2 - 1;

        x = Math.round(nx * RANGE);
        y = Math.round(ny * RANGE);

        if (queued || !visible.size) return;
        queued = true;
        requestAnimationFrame(apply);
    }, { passive: true });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
    init();
}
